import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Section, StatusBadge, ApprovalTimeline } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash, Plus } from "@phosphor-icons/react";

const blankEntry = () => ({ _rowKey: crypto.randomUUID(), project_id: "", task: "", start_time: "", end_time: "", break_hours: 0, hours: 0, description: "", billable: true });

// Timezone-safe date math: build and read dates purely in UTC so the day never
// shifts when the browser is in a non-UTC timezone (e.g. Gulf UTC+4).
function mondayOf(iso) {
  const [y, m, dd] = iso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1, dd));
  const day = d.getUTCDay() === 0 ? 7 : d.getUTCDay(); // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const [y, m, dd] = iso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1, dd));
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
// Local "today" as YYYY-MM-DD (no UTC shift — matches the <input type=date>).
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function EmpTimesheet() {
  const [sp, setSp] = useSearchParams();
  const initialDate = sp.get("date") || new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(initialDate);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([blankEntry()]);
  const [settings, setSettings] = useState({ entry_method: "both", daily_hours: 8 });
  const [week, setWeek] = useState({ status: "draft", total_hours: 0, billable_hours: 0, per_project: [], days: [], week_start: "", week_end: "", rejection_reason: "" });
  const [view, setView] = useState("week");
  const [showTl, setShowTl] = useState(false);
  const [month, setMonth] = useState(() => initialDate.slice(0, 7));
  const [monthData, setMonthData] = useState({ days: [], current_week_start: "" });

  const weekStart = mondayOf(date);
  const currentWeekStart = mondayOf(todayIso());
  const isCurrentWeek = weekStart === currentWeekStart;

  const loadDay = useCallback(async (d) => {
    try {
      const ts = await api.get(`/timesheets/by-date?date=${d}`);
      // Attach a stable per-row key so React reconciles correctly when the
      // user inserts or deletes rows mid-list. Prefer the server-side entry
      // id when present; fall back to a fresh UUID for brand new rows.
      const withKey = (list) => list.map((e) => ({ ...e, _rowKey: e._rowKey || e.id || crypto.randomUUID() }));
      if (ts.data && ts.data.entries) {
        setEntries(ts.data.entries.length ? withKey(ts.data.entries) : [blankEntry()]);
      } else {
        setEntries([blankEntry()]);
      }
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  const loadWeek = useCallback(async (ws) => {
    try {
      const r = await api.get(`/timesheets/weeks/current?week_start=${ws}`);
      setWeek(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  const loadStatic = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/projects"), api.get("/settings")]);
      setProjects(p.data.filter((x) => x.status === "Active"));
      setSettings(s.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  const loadMonth = useCallback(async (mo) => {
    try {
      const r = await api.get(`/timesheets/month?month=${mo}`);
      setMonthData(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { loadDay(date); loadWeek(weekStart); }, [date, weekStart, loadDay, loadWeek]);
  useEffect(() => { if (view === "month") loadMonth(month); }, [view, month, loadMonth]);
  useEffect(() => {
    const q = sp.get("date");
    if (q && q !== date) setDate(q);
  }, [sp, date]);

  const weekLocked = week.status === "submitted" || week.status === "admin_approved" || week.status === "approved";
  // Employees may only edit the current week's hours (enforced on the backend too).
  const dayLocked = weekLocked || !isCurrentWeek;

  const updateEntry = (i, k, v) => {
    const next = [...entries];
    next[i] = { ...next[i], [k]: v };
    if ((k === "start_time" || k === "end_time" || k === "break_hours") && next[i].start_time && next[i].end_time) {
      const [sh, sm] = next[i].start_time.split(":").map(Number);
      const [eh, em] = next[i].end_time.split(":").map(Number);
      const diff = (eh * 60 + em - sh * 60 - sm) / 60 - (parseFloat(next[i].break_hours) || 0);
      if (!isNaN(diff) && diff >= 0) next[i].hours = Math.round(diff * 100) / 100;
    }
    setEntries(next);
  };
  const addRow = () => setEntries([...entries, blankEntry()]);
  const removeRow = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const dayTotal = entries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  const dayBreak = entries.reduce((s, e) => s + (parseFloat(e.break_hours) || 0), 0);

  const saveDay = async () => {
    const payload = {
      date, submit: false,
      entries: entries.filter((e) => e.project_id && e.hours > 0).map(({ _rowKey, ...e }) => ({
        ...e, hours: parseFloat(e.hours) || 0, break_hours: parseFloat(e.break_hours) || 0,
      })),
    };
    if (payload.entries.length === 0) return toast.error("Add at least one project entry");
    try {
      await api.post("/timesheets", payload);
      toast.success("Daily draft saved");
      await loadWeek(weekStart);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitWeek = async () => {
    if (!window.confirm(`Submit the entire week ${week.week_start} – ${week.week_end} for approval?\n\nTotal: ${week.total_hours}h across ${week.days_count || 0} day(s). You will not be able to edit until the admin reviews.`)) return;
    try {
      await api.post("/timesheets/weeks/submit", { week_start: weekStart });
      toast.success("Week submitted for approval");
      await loadWeek(weekStart);
      await loadDay(date);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const showStartEnd = settings.entry_method === "start_end" || settings.entry_method === "both";

  // Build week day strip: Mon..Sun with per-day hour hints
  const weekDays = [...Array(7)].map((_, i) => {
    const d = addDays(weekStart, i);
    const found = (week.days || []).find((x) => x.date === d);
    return { date: d, hours: found ? found.total_hours : 0, isActive: d === date,
             label: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i] };
  });

  return (
    <div data-testid="emp-timesheet">
      <PageHeader overline="Weekly timesheet" title="My Timesheet"
        subtitle="Save daily drafts, then submit the full week for approval."
        actions={
          <>
            <div className="flex border border-[#E5E5E5]">
              <button onClick={() => setView("week")} data-testid="view-week"
                className={`px-3 h-10 text-xs font-semibold ${view === "week" ? "bg-[#1F4E8C] text-white" : "bg-white text-[#1F4E8C]"}`}>Week</button>
              <button onClick={() => setView("month")} data-testid="view-month"
                className={`px-3 h-10 text-xs font-semibold ${view === "month" ? "bg-[#1F4E8C] text-white" : "bg-white text-[#1F4E8C]"}`}>Month</button>
            </div>
            {view === "month" ? (
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                     data-testid="timesheet-month" className="rounded-none border-[#E5E5E5] w-40" />
            ) : (
              <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setSp({ date: e.target.value }); }}
                     data-testid="timesheet-date" className="rounded-none border-[#E5E5E5] w-44" />
            )}
            <StatusBadge status={week.status} direct={!week.reviewed_by} />
          </>
        } />

      {view === "month" && (
        <MonthCalendar month={month} days={monthData.days}
          onPick={(d) => { setDate(d); setSp({ date: d }); setView("week"); }} />
      )}

      {view === "week" && (<>
      {!isCurrentWeek && !weekLocked && (
        <div className="mb-5 border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3" data-testid="past-week-notice">
          You're viewing a past week. Only the current week ({currentWeekStart}) can be edited.
        </div>
      )}

      {/* Week status + submit panel */}
      <div className="mb-5 border border-[#1F4E8C] bg-white p-4 lg:p-5" data-testid="week-panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="eyebrow mb-1">Week of {week.week_start} → {week.week_end}</div>
            <div className="font-head text-2xl font-semibold">
              {week.total_hours}h <span className="text-neutral-500 text-sm font-normal">· {week.billable_hours}h billable · {week.days_count || 0} day(s) logged</span>
            </div>
            {week.rejection_reason && week.status === "rejected" && (
              <div className="mt-2 text-sm text-red-700" data-testid="week-rejection-reason">
                <strong>Reviewer:</strong> {week.rejection_reason}
              </div>
            )}
            {week.status === "submitted" && (
              <div className="mt-1 text-xs text-neutral-600" data-testid="week-status-note">Awaiting your assigned admin's approval. Daily entries are locked.</div>
            )}
            {week.status === "admin_approved" && (
              <div className="mt-1 text-xs text-sky-700" data-testid="week-status-note">
                {week.reviewed_by ? `Approved by ${week.reviewed_by} — awaiting final Super Admin approval. Locked.` : "Sent to Super Admin for approval. Locked."}
              </div>
            )}
            {week.status === "approved" && (
              <div className="mt-1 text-xs text-emerald-700">Approved by {week.reviewed_by || "Admin"}. Locked.</div>
            )}
            {week.events && week.events.length > 0 && week.status !== "draft" && (
              <button onClick={() => setShowTl((v) => !v)} data-testid="timesheet-track"
                className="mt-2 text-xs font-semibold text-[#1F4E8C] underline underline-offset-2">
                {showTl ? "Hide timeline" : "Track approval"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!weekLocked && (
              <Button onClick={submitWeek} data-testid="submit-week"
                      className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white h-10 px-5 font-semibold">
                Submit Week
              </Button>
            )}
          </div>
        </div>
        {showTl && week.events && week.events.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#F0F0EE]" data-testid="timesheet-timeline-wrap">
            <div className="eyebrow mb-3 text-neutral-500">Approval timeline</div>
            <ApprovalTimeline events={week.events} />
          </div>
        )}
        {/* Day strip */}
        <div className="mt-4 grid grid-cols-7 gap-1">
          {weekDays.map((d) => (
            <button key={d.date} onClick={() => { setDate(d.date); setSp({ date: d.date }); }}
                    data-testid={`weekday-${d.label.toLowerCase()}`}
                    className={`border p-2 text-left transition-colors ${d.isActive ? "border-[#FF5500] bg-[#FFF4EC]" : "border-[#E5E5E5] bg-white hover:bg-[#F7F7F5]"}`}>
              <div className="eyebrow">{d.label}</div>
              <div className="text-xs text-neutral-500">{d.date.slice(5)}</div>
              <div className="font-head text-lg font-semibold mt-1">{d.hours || 0}<span className="text-xs text-neutral-500">h</span></div>
            </button>
          ))}
        </div>
        {/* Per-project rollup */}
        {week.per_project && week.per_project.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#F0F0EE]">
            <div className="eyebrow mb-2">Week by project</div>
            <div className="flex flex-wrap gap-2">
              {week.per_project.map((p) => (
                <div key={p.project_id} className="px-3 py-1.5 border border-[#1F4E8C] text-xs" data-testid={`week-project-${p.project_id}`}>
                  <span className="font-semibold">{p.project_name}</span>
                  <span className="text-neutral-600"> · {p.hours}h</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Section title={`Entries for ${date}`}
        right={
          <div className="text-sm">
            Day <span className="font-head font-semibold text-[#FF5500]">{dayTotal.toFixed(2)}h</span> + Break <span className="font-semibold">{dayBreak}h</span>
          </div>
        }>
        <div className="overflow-x-auto">
          <table className="w-full table-dense border-collapse">
            <thead>
              <tr className="border-b-2 border-[#1F4E8C]">
                <th className="eyebrow text-left">Project</th>
                <th className="eyebrow text-left">Task</th>
                {showStartEnd && <><th className="eyebrow text-left">Start</th><th className="eyebrow text-left">End</th></>}
                <th className="eyebrow text-left">Break</th>
                <th className="eyebrow text-left">Hours</th>
                <th className="eyebrow text-left">Billable</th>
                <th className="eyebrow text-left">Description</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={e._rowKey} className="border-b border-[#F0F0EE]">
                  <td className="min-w-[180px]">
                    <select disabled={dayLocked} value={e.project_id} onChange={(ev) => updateEntry(i, "project_id", ev.target.value)}
                      data-testid={`entry-project-${i}`}
                      className="w-full h-9 border border-[#E5E5E5] px-2 text-sm bg-white">
                      <option value="">Select project…</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.project_id} — {p.name}</option>)}
                    </select>
                  </td>
                  <td><Input disabled={dayLocked} value={e.task} onChange={(ev) => updateEntry(i, "task", ev.target.value)} className="rounded-none border-[#E5E5E5] h-9" data-testid={`entry-task-${i}`} /></td>
                  {showStartEnd && <>
                    <td><Input disabled={dayLocked} type="time" value={e.start_time} onChange={(ev) => updateEntry(i, "start_time", ev.target.value)} className="rounded-none border-[#E5E5E5] h-9 w-28" /></td>
                    <td><Input disabled={dayLocked} type="time" value={e.end_time} onChange={(ev) => updateEntry(i, "end_time", ev.target.value)} className="rounded-none border-[#E5E5E5] h-9 w-28" /></td>
                  </>}
                  <td><Input disabled={dayLocked} type="number" step="0.25" value={e.break_hours} onChange={(ev) => updateEntry(i, "break_hours", ev.target.value)} className="rounded-none border-[#E5E5E5] h-9 w-20" /></td>
                  <td><Input disabled={dayLocked} type="number" step="0.25" value={e.hours} onChange={(ev) => updateEntry(i, "hours", ev.target.value)} className="rounded-none border-[#E5E5E5] h-9 w-24" data-testid={`entry-hours-${i}`} /></td>
                  <td className="text-center">
                    <input disabled={dayLocked} type="checkbox" checked={e.billable} onChange={(ev) => updateEntry(i, "billable", ev.target.checked)} className="accent-[#FF5500]" />
                  </td>
                  <td><Input disabled={dayLocked} value={e.description} onChange={(ev) => updateEntry(i, "description", ev.target.value)} className="rounded-none border-[#E5E5E5] h-9" /></td>
                  <td>
                    {!dayLocked && entries.length > 1 && (
                      <button onClick={() => removeRow(i)} className="text-neutral-500 hover:text-red-600" data-testid={`remove-entry-${i}`}><Trash size={16} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!dayLocked && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#E5E5E5]">
            <Button variant="outline" onClick={addRow} data-testid="add-project-row" className="rounded-none border-[#1F4E8C]">
              <Plus size={16} className="mr-1" /> Add Project
            </Button>
            <div className="flex gap-2">
              <Button onClick={saveDay} data-testid="save-draft" className="rounded-none bg-[#1F4E8C] hover:bg-[#163a68] text-white">Save Day</Button>
            </div>
          </div>
        )}
      </Section>
      </>)}
    </div>
  );
}

function MonthCalendar({ month, days, onPick }) {
  const byDate = {};
  (days || []).forEach((d) => { byDate[d.date] = d; });
  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const firstDow = first.getUTCDay() === 0 ? 7 : first.getUTCDay(); // Mon=1..Sun=7
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells = [];
  for (let i = 1; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${month}-${String(d).padStart(2, "0")}`);
  }
  const statusColor = { approved: "border-emerald-300 bg-emerald-50", submitted: "border-amber-300 bg-amber-50", rejected: "border-red-300 bg-red-50" };
  return (
    <div className="border border-[#1F4E8C] bg-white p-4 lg:p-5" data-testid="month-calendar">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="eyebrow text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((iso, idx) => {
          if (!iso) return <div key={`e${idx}`} />;
          const info = byDate[iso];
          const hrs = info ? info.total_hours : 0;
          const ws = info?.week_status;
          const isToday = iso === todayIso();
          return (
            <button key={iso} onClick={() => onPick(iso)} data-testid={`cal-day-${iso}`}
              className={`min-h-[64px] border p-2 text-left transition-colors hover:border-[#FF5500] ${statusColor[ws] || "border-[#E5E5E5] bg-white"} ${isToday ? "ring-2 ring-[#FF5500]" : ""}`}>
              <div className="text-xs text-neutral-500">{iso.slice(8)}</div>
              {hrs > 0 && <div className="font-head text-base font-semibold mt-1">{hrs}<span className="text-xs text-neutral-500">h</span></div>}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-neutral-600">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border border-amber-300 bg-amber-50" /> Submitted</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border border-emerald-300 bg-emerald-50" /> Approved</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border border-red-300 bg-red-50" /> Rejected</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border-2 border-[#FF5500]" /> Today</span>
      </div>
    </div>
  );
}
